import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { FilterItemTypes } from "configs/types";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { useAppSelector } from "store/index";

import Filter from "components/global/Filter/Filter";

import gridIcon from "assets/icons/grid-icon.svg";

interface Props {
  searchValue: string;
  handleSearch: (e: string) => void;
  handleUpdateProviders: (
    e: { value: string | number; label: string }[],
  ) => void;
  selectedProviders: { value: string | number; label: string }[];
  handleUpdateCollections: (
    e: { value: string | number; label: string }[],
  ) => void;
  selectedCollections: { value: string | number; label: string }[];
  handleUpdateTags: (e: { value: string | number; label: string }[]) => void;
  selectedTags: { value: string | number; label: string }[];
  handleUpdateGeos: (e: { value: string | number; label: string }[]) => void;
  selectedGeos: { value: string | number; label: string }[];
  handleApply: () => void;
  typeValue: string;
  handleType: (value: string) => void;
  selectedFreeSpins: { value: string | number; label: string }[];
  handleUpdateFreeSpins: (
    e: { value: string | number; label: string }[],
  ) => void;
}
const GamesList: React.FC<Props> = ({
  searchValue,
  handleSearch,
  handleUpdateProviders,
  selectedProviders,
  handleUpdateCollections,
  selectedCollections,
  handleUpdateTags,
  selectedTags,
  handleUpdateGeos,
  selectedGeos,
  handleApply,
  typeValue,
  handleType,
  selectedFreeSpins,
  handleUpdateFreeSpins,
}) => {
  const { t } = useTranslation();

  const geoList = useAppSelector((state) => state.settings.geoList);

  const [providers, setProviders] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [collections, setCollections] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [tags, setTags] = useState<{ value: string | number; label: string }[]>(
    [],
  );
  const [geos, setGeos] = useState<{ value: string | number; label: string }[]>(
    [],
  );

  const getProviders = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.providersList}?limit=500&offset=0`,
      );
      const array = result.data.items.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setProviders(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  const getCollections = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.collectionsList}?limit=500&offset=0`,
      );
      const array = result.data.items.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setCollections(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  const getTags = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.tagsList}?limit=500&offset=0`,
      );
      const array = result.data.items.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setTags(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  useEffect(() => {
    getProviders();
    getCollections();
    getTags();
  }, []);
  useEffect(() => {
    const result = geoList.map((el) => {
      return {
        value: el,
        label: el,
      };
    });
    setGeos(result);
  }, [geoList]);

  const handleApplyProviders = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateProviders(isDelete ? e : [...selectedProviders, ...e]);
  };
  const handleApplyCollections = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateCollections(isDelete ? e : [...selectedCollections, ...e]);
  };
  const handleApplyTags = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateTags(isDelete ? e : [...selectedTags, ...e]);
  };
  const handleApplyGeos = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateGeos(isDelete ? e : [...selectedGeos, ...e]);
  };

  const handleClearFilters = () => {
    handleUpdateGeos([]);
    handleUpdateTags([]);
    handleUpdateCollections([]);
    handleUpdateProviders([]);
    handleUpdateFreeSpins([{ value: 2, label: t("all") }]);
    handleSearch("");
    handleType("");
    handleApply();
  };

  const handleApplyFilters = () => {
    handleApply();
  };

  return (
    <Filter
      className="games-filter"
      handleClear={handleClearFilters}
      handleApply={handleApplyFilters}
      search={searchValue}
      onSearch={handleSearch}
      placeholder={t("searchGame")}
      searchLabel={t("title")}
      filterItems={[
        {
          type: FilterItemTypes.input,
          title: t("type"),
          boxTitle: t("type"),
          icon: gridIcon,
          inputValue: typeValue,
          setInputValue: handleType,
        },
        {
          type: FilterItemTypes.radio,
          title: t("freeSpins"),
          boxTitle: t("freeSpins"),
          icon: gridIcon,
          itemsArray: [
            { value: 2, label: t("all") },
            { value: 1, label: t("yes") },
            { value: 0, label: t("no") },
          ],
          activeItems: selectedFreeSpins,
          handleApply: handleUpdateFreeSpins,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("providers"),
          boxTitle: t("filterProvider"),
          icon: gridIcon,
          itemsArray: providers,
          activeItems: selectedProviders,
          handleApply: handleApplyProviders,
          disabled: providers.length === 0,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("collections"),
          boxTitle: t("filterCollections"),
          icon: gridIcon,
          itemsArray: collections,
          activeItems: selectedCollections,
          handleApply: handleApplyCollections,
          disabled: collections.length === 0,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("tags"),
          boxTitle: t("filterTags"),
          icon: gridIcon,
          itemsArray: tags,
          activeItems: selectedTags,
          handleApply: handleApplyTags,
          disabled: tags.length === 0,
        },
        {
          type: FilterItemTypes.multiselect,
          title: "GEO",
          boxTitle: t("filterGeos"),
          icon: gridIcon,
          itemsArray: geos,
          activeItems: selectedGeos,
          handleApply: handleApplyGeos,
          disabled: geos.length === 0,
        },
      ]}
    />
  );
};

export default GamesList;
